import "./Quotes.css";
import Loading from "./Loading";
import { useEffect, useState } from "react";
import { arrRandomVal } from "./utils";

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 0 1 15.5-6.36L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.36L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const ShareIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const CopyIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const Quotes = ({ quotes, newBgColor }) => {
  const [quote, setQuote] = useState(null);
  const [fading, setFading] = useState(false);
  const [copied, setCopied] = useState(false);

  const pickNextQuote = (prev) => {
    let next = arrRandomVal(quotes);
    if (quotes.length > 1 && next === prev) return pickNextQuote(prev);
    return next;
  };

  const transition = (fn) => {
    setFading(true);
    setTimeout(() => {
      fn();
      setFading(false);
    }, 280);
  };

  const handleNewQuote = () => {
    transition(() => {
      setQuote((prev) => pickNextQuote(prev));
      newBgColor();
    });
  };

  const handleShare = () => {
    if (!quote) return;
    const text = `"${quote.content}" — ${quote.author}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    if (!quote) return;
    const text = `"${quote.content}" — ${quote.author}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked */
    }
  };

  useEffect(() => {
    setQuote(pickNextQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!quote) return <Loading />;

  return (
    <article
      id="quote-box"
      className={`quote-card ${fading ? "is-fading" : ""}`}
    >
      <div className="quote-meta">
        <span className="quote-eyebrow">Reflection</span>
        <span className="quote-counter" aria-live="polite">
          {String(quotes.indexOf(quote) + 1).padStart(2, "0")}{" "}
          <span className="quote-counter-sep">/</span>{" "}
          {String(quotes.length).padStart(2, "0")}
        </span>
      </div>

      <blockquote className="quote-body">
        <span className="quote-mark" aria-hidden="true">
          &ldquo;
        </span>
        <p className="quote-text">{quote.content}</p>
      </blockquote>

      <div className="quote-footer">
        <div className="quote-attribution">
          <span className="attribution-rule" aria-hidden="true" />
          <span className="author" id="author">
            {quote.author}
          </span>
        </div>

        <div className="quote-actions">
          <button
            type="button"
            className="icon-button"
            onClick={handleCopy}
            aria-label="Copy quote"
            title="Copy"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={handleShare}
            aria-label="Share quote"
            title="Share"
          >
            <ShareIcon />
          </button>
          <button
            type="button"
            className="primary-action"
            onClick={handleNewQuote}
            id="new-quote"
            aria-label="Show another quote"
          >
            <RefreshIcon />
            <span>New quote</span>
          </button>
        </div>
      </div>
    </article>
  );
};

export default Quotes;
